import { ColumnDef } from "@tanstack/react-table";
import { ROLES_TO_TEXT, STATUS_TYPES } from "common/utils/users";
import {
  GetUserInvitesAndUsersQuery,
  UserAuthMethod,
} from "../../../generated/graphql";
import { Account } from "../../Authorization/types";
import {
  LegacyTableStateFromURL,
  ManualPaginationConfig,
} from "../../Common/Tables/hooks";
import Table from "../../Common/Tables/Table";
import UserActions from "../UserActions";
import UserInviteActions from "../UserInviteActions";
import { ButtonWrapper, OptionsContainer } from "./__styles__/TeamTable";

const LOADING_TEXT = "Loading users";
const NO_DATA_TEXT = "No users found";

type QueryResult = Partial<
  Pick<GetUserInvitesAndUsersQuery["usersAndInvites"], "data">
>;
export type UserOrUserInvite = NonNullable<QueryResult["data"]>[number];

const Name = (row: UserOrUserInvite) => {
  return `${row.firstName} ${row.lastName}`;
};

const AuthMethod = (row: UserOrUserInvite) => {
  switch (row.authMethod) {
    case UserAuthMethod.PASSWORD:
      return "Password";
    case UserAuthMethod.SSO:
      return "Single Sign On";
  }
};

const Status = (row: UserOrUserInvite) => {
  if (row.__typename === "UserInvite") return STATUS_TYPES.INVITED;
  if (row.suspended === true) return STATUS_TYPES.SUSPENDED;
  return STATUS_TYPES.ACTIVE;
};

const Pii = (row: UserOrUserInvite) => {
  return row.canViewPersonalIdentifiableInformation ? "Can view" : (
      "Cannot view"
    );
};

const Role = (row: UserOrUserInvite) => {
  return ROLES_TO_TEXT[row.role.name];
};

type ActionButtonProps = {
  row: UserOrUserInvite;
  onUpdate: () => {};
};
const ActionButton = ({ row, onUpdate }: ActionButtonProps) => {
  if (row.__typename === "UserInvite") {
    return (
      <OptionsContainer>
        <ButtonWrapper>
          <UserInviteActions
            id={row.id}
            firstName={row.firstName}
            lastName={row.lastName}
            email={row.email}
            onUpdate={onUpdate}
          />
        </ButtonWrapper>
      </OptionsContainer>
    );
  }

  return (
    <OptionsContainer>
      <ButtonWrapper>
        <UserActions user={row} onUpdate={onUpdate} />
      </ButtonWrapper>
    </OptionsContainer>
  );
};

export interface TeamTableProps {
  account: Maybe<Account>;
  previousData?: Array<UserOrUserInvite>;
  currentData: Array<UserOrUserInvite>;
  loading: boolean;
  manualPaginationConfig: ManualPaginationConfig;
  onUpdate: () => {};
  initialTableState: LegacyTableStateFromURL;
  setTableStateInURL: (state: any) => void;
}

export default ({
  account,
  previousData,
  currentData,
  loading,
  manualPaginationConfig,
  onUpdate,
  initialTableState,
  setTableStateInURL,
}: TeamTableProps) => {
  let columns: Array<ColumnDef<UserOrUserInvite>> = [
    {
      id: "name",
      header: "Name",
      accessorFn: Name,
    },
    {
      id: "email",
      header: "Email",
      accessorKey: "email",
    },
    {
      id: "status",
      header: "Status",
      accessorFn: Status,
    },
    {
      id: "role",
      header: "Role",
      accessorFn: Role,
    },
    ...(account?.isSSOEnabled ?
      [
        {
          id: "authMethod",
          header: "Authentication Method",
          accessorFn: AuthMethod,
        },
      ]
    : []),
    {
      id: "actions",
      header: "",
      cell: row => <ActionButton onUpdate={onUpdate} row={row.row.original} />,
    },
  ];

  if (account?.repetitiveLossEnabled) {
    columns.splice(3, 0, {
      id: "pii",
      header: "PII",
      accessorFn: Pii,
    });
  }

  return (
    <Table
      previousData={previousData}
      currentData={currentData}
      columns={columns}
      tableStyleDetails={{ hasHighlights: true, hasRowActions: true }}
      loadingDetails={{
        loading,
        loadingText: LOADING_TEXT,
        noDataText: NO_DATA_TEXT,
      }}
      initialState={initialTableState}
      manualPaginationConfig={manualPaginationConfig}
      setTableStateInURL={setTableStateInURL}
    />
  );
};
